import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { fadeUp, viewportOnce } from "../lib/motion";
import { trackEvent } from "../lib/seo";
import { QUOTE_ANALYSIS } from "../lib/product";
import { HERO_IMAGES } from "../lib/product";
import { STATUS_TONES } from "./product/shared";

const DEMO_TABS = ["HOME VISION", "BOQ REPORT", "QUOTE ANALYSIS"];

const BOQ_ROWS = [
    { item: "Preliminaries & general", unit: "item", qty: "1", amount: "R48,500" },
    { item: "Earthworks & foundations", unit: "m³", qty: "68", amount: "R96,400" },
    { item: "Masonry & brickwork", unit: "m²", qty: "214", amount: "R187,300" },
    { item: "Roof structure & coverings", unit: "m²", qty: "186", amount: "R142,750" },
    { item: "Windows & external doors", unit: "no.", qty: "14", amount: "R118,900" },
    { item: "Internal finishes", unit: "item", qty: "1", amount: "R268,400" },
    { item: "Electrical & plumbing", unit: "item", qty: "1", amount: "R154,200" },
];

const HomeDemo = () => {
    const [tab, setTab] = useState("HOME VISION");

    return (
        <section
            data-testid="home-demo-section"
            className="relative bg-ink py-24 lg:py-32"
        >
            <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16">
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                    className="flex flex-wrap items-center justify-between gap-6"
                >
                    <p className="flex items-center gap-3 font-mono text-[13px] tracking-[0.3em] text-mist">
                        <span className="h-1.5 w-1.5 bg-accent" />
                        PRODUCT DEMONSTRATION
                    </p>
                    <span className="border border-white/20 px-2.5 py-1 font-mono text-[13px] tracking-[0.22em] text-mist">
                        SIMULATED DEMONSTRATION — ILLUSTRATIVE DATA
                    </span>
                </motion.div>

                <motion.h2
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                    data-testid="demo-headline"
                    className="mt-12 max-w-4xl font-display text-4xl font-semibold leading-[1.1] text-white md:text-6xl"
                >
                    See what using BuildEase looks like
                    <span className="text-accent">.</span>
                </motion.h2>

                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                    data-testid="demo-panel"
                    className="mt-14 border border-white/12 bg-[#0C1428]"
                >
                    <div
                        role="tablist"
                        aria-label="Product demonstration views"
                        className="flex flex-wrap items-center gap-2 border-b border-white/10 px-5 py-4"
                    >
                        {DEMO_TABS.map((t) => (
                            <button
                                key={t}
                                role="tab"
                                aria-selected={tab === t}
                                data-testid={`demo-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
                                onClick={() => {
                                    setTab(t);
                                    trackEvent(
                                        `demo_interaction_${t
                                            .toLowerCase()
                                            .replace(/\s+/g, "_")}`,
                                    );
                                }}
                                className={`px-4 py-2 font-mono text-[13px] tracking-[0.2em] transition-colors duration-300 ${
                                    tab === t
                                        ? "bg-accent/10 font-semibold text-accent"
                                        : "text-cad hover:text-white"
                                }`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>

                    {tab === "HOME VISION" ? (
                        <div className="grid lg:grid-cols-[1fr_260px]">
                            <div className="relative">
                                <img
                                    src={HERO_IMAGES.house3d}
                                    alt="Home Vision demonstration — a contemporary home visual concept"
                                    loading="lazy"
                                    className="aspect-[16/9] w-full object-cover"
                                />
                                <span className="absolute left-4 top-4 border border-white/30 bg-[#0A0F1D]/70 px-2.5 py-1 font-mono text-[13px] tracking-[0.2em] text-white/90 backdrop-blur-sm">
                                    3D · EXTERIOR
                                </span>
                            </div>
                            <div className="border-t border-white/10 px-5 py-5 lg:border-l lg:border-t-0">
                                <p className="font-mono text-[13px] tracking-[0.22em] text-white">
                                    DESIGN TO COST IMPACT
                                </p>
                                <p className="mt-4 font-mono text-[13px] tracking-[0.18em] text-cad">
                                    CURRENT ESTIMATE (DEMO)
                                </p>
                                <p className="mt-1 font-display text-2xl font-semibold text-white">
                                    R2,450,000
                                </p>
                                <div className="mt-5 border-t border-white/10 pt-4 text-[13px] leading-relaxed text-mist">
                                    <p className="flex justify-between gap-3">
                                        <span>Exterior wall change</span>
                                        <span className="font-mono text-accent">
                                            +R85,420
                                        </span>
                                    </p>
                                    <p className="mt-2 flex justify-between gap-3">
                                        <span>Roof (no change)</span>
                                        <span className="font-mono text-cad">
                                            R0
                                        </span>
                                    </p>
                                </div>
                                <p className="mt-4 font-mono text-[13px] leading-relaxed tracking-[0.16em] text-cad">
                                    ILLUSTRATIVE — NOT A LIVE ESTIMATE
                                </p>
                            </div>
                        </div>
                    ) : tab === "BOQ REPORT" ? (
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[640px] text-left">
                                <thead>
                                    <tr className="border-b border-white/10 font-mono text-[13px] tracking-[0.2em] text-cad">
                                        <th className="px-5 py-3.5 font-medium">
                                            SECTION
                                        </th>
                                        <th className="px-5 py-3.5 font-medium">
                                            UNIT
                                        </th>
                                        <th className="px-5 py-3.5 font-medium">
                                            QTY
                                        </th>
                                        <th className="px-5 py-3.5 text-right font-medium">
                                            AMOUNT (DEMO)
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {BOQ_ROWS.map((r) => (
                                        <tr
                                            key={r.item}
                                            className="border-b border-white/[0.06] text-[14px]"
                                        >
                                            <td className="px-5 py-3.5 text-white/90">
                                                {r.item}
                                            </td>
                                            <td className="px-5 py-3.5 font-mono text-[13px] text-mist">
                                                {r.unit}
                                            </td>
                                            <td className="px-5 py-3.5 font-mono text-[13px] text-mist">
                                                {r.qty}
                                            </td>
                                            <td className="px-5 py-3.5 text-right font-mono text-[13px] text-white/90">
                                                {r.amount}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <p className="px-5 py-4 font-mono text-[13px] leading-relaxed tracking-[0.16em] text-cad">
                                SAMPLE BOQ SECTIONS — SEE THE FULL SAMPLE
                                REPORT FOR ASSUMPTIONS &amp; EXCLUSIONS
                            </p>
                        </div>
                    ) : (
                        <div>
                            <ul className="px-5 py-2">
                                {QUOTE_ANALYSIS.map((row) => {
                                    const tone = STATUS_TONES[row.tone];
                                    return (
                                        <li
                                            key={row.item}
                                            className="flex flex-wrap items-center justify-between gap-3 border-b border-white/[0.06] py-4"
                                        >
                                            <span className="text-[15px] text-white/90">
                                                {row.item}
                                            </span>
                                            <span
                                                className={`inline-flex items-center gap-2 border px-2.5 py-1 font-mono text-[13px] tracking-[0.16em] ${tone.border} ${tone.text}`}
                                            >
                                                <span
                                                    className={`h-1 w-1 rounded-full ${tone.dot}`}
                                                />
                                                {row.state}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                            <p className="px-5 py-4 font-mono text-[13px] leading-relaxed tracking-[0.16em] text-cad">
                                NEUTRAL ASSESSMENTS — NOT A JUDGEMENT OF ANY
                                CONTRACTOR
                            </p>
                        </div>
                    )}
                </motion.div>

                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                    className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-4"
                >
                    <Link
                        to="/join-the-pilot"
                        data-testid="home-demo-cta"
                        onClick={() => trackEvent("get_started_click", "home_demo")}
                        className="group inline-flex items-center gap-2 bg-accent px-6 py-3.5 text-[15px] font-semibold text-white transition-colors duration-300 hover:bg-[#ff7026]"
                    >
                        Get Started
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                    </Link>
                    <Link
                        to="/sample-report"
                        data-testid="home-demo-report-link"
                        className="text-[15px] font-semibold text-accent transition-colors duration-300 hover:text-[#ff7026]"
                    >
                        See a full sample BuildEase report →
                    </Link>
                </motion.div>
            </div>
        </section>
    );
};

export default HomeDemo;
